import React, { useState, useMemo } from 'react';
import { IonButton, IonIcon } from '@ionic/react';
import { checkmarkCircle, closeCircle, bulbOutline } from 'ionicons/icons';
import { soundService } from '../services/SoundService';
import { hapticService } from '../services/HapticService';
import './QuizRenderer.css';

/**
 * QuizRenderer Component
 * Renders interactive quiz questions with multiple choice
 * Options are shuffled randomly to prevent predictable answer positions
 */
function QuizRenderer({
  question,
  options = [],
  correctAnswer,
  explanation,
  onAnswer,
  onNext,
  points = 25
}) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  // Shuffle options and track correct answer position
  // This ensures the correct answer isn't always in the same position
  const { shuffledOptions, shuffledCorrectIndex, correctAnswerText } = useMemo(() => {
    if (!options || options.length === 0) {
      return { shuffledOptions: [], shuffledCorrectIndex: -1, correctAnswerText: '' };
    }

    // Get the original correct index
    const originalCorrectIndex = typeof correctAnswer === 'number' 
      ? correctAnswer 
      : options.findIndex(opt => opt === correctAnswer);
    
    // Get the correct answer text before shuffling
    const answerText = options[originalCorrectIndex] || correctAnswer;

    // Create array with original indices for tracking
    const optionsWithIndex = options.map((opt, idx) => ({ 
      option: opt, 
      originalIndex: idx 
    }));
    
    // Fisher-Yates shuffle algorithm for true randomization
    for (let i = optionsWithIndex.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [optionsWithIndex[i], optionsWithIndex[j]] = [optionsWithIndex[j], optionsWithIndex[i]];
    }
    
    // Find the new position of the correct answer after shuffling
    const newCorrectIndex = optionsWithIndex.findIndex(
      item => item.originalIndex === originalCorrectIndex
    );
    
    return {
      shuffledOptions: optionsWithIndex.map(item => item.option),
      shuffledCorrectIndex: newCorrectIndex,
      correctAnswerText: answerText
    };
  }, [options, correctAnswer]);

  const isCorrect = selectedIndex === shuffledCorrectIndex;

  const handleSelect = (index) => {
    if (showResult) return;
    soundService.playClick();
    hapticService.light();
    setSelectedIndex(index);
  };

  const handleSubmit = () => {
    if (selectedIndex === null) return;
    setShowResult(true);
    
    // Play sound and haptic based on result
    if (isCorrect) {
      soundService.playSuccess();
      hapticService.success();
    } else {
      soundService.playError();
      hapticService.error();
    }
    
    if (onAnswer) {
      onAnswer(isCorrect, points);
    }
  };

  const handleNext = () => {
    soundService.playClick();
    hapticService.light();
    setSelectedIndex(null);
    setShowResult(false);
    setShowExplanation(false);
    if (onNext) onNext();
  };

  const getOptionClass = (index) => {
    let classes = 'quiz-option';
    if (selectedIndex === index) {
      classes += ' selected';
    }
    if (showResult) {
      if (index === shuffledCorrectIndex) {
        classes += ' correct';
      } else if (index === selectedIndex && index !== shuffledCorrectIndex) {
        classes += ' incorrect';
      }
    }
    return classes;
  };

  return (
    <div className="quiz-renderer">
      <div className="quiz-question">
        <h3>{question}</h3>
      </div>

      <div className="quiz-options">
        {shuffledOptions.map((option, index) => (
          <button
            key={index}
            className={getOptionClass(index)}
            onClick={() => handleSelect(index)}
            disabled={showResult}
          >
            <span className="option-letter">
              {String.fromCharCode(65 + index)}
            </span>
            <span className="option-text">{option}</span>
            {showResult && index === shuffledCorrectIndex && (
              <IonIcon icon={checkmarkCircle} className="result-icon correct" />
            )}
            {showResult && index === selectedIndex && index !== shuffledCorrectIndex && (
              <IonIcon icon={closeCircle} className="result-icon incorrect" />
            )}
          </button>
        ))}
      </div>

      {showResult && (
        <div className={`quiz-result ${isCorrect ? 'correct' : 'incorrect'}`}>
          <div className="result-header">
            <IonIcon icon={isCorrect ? checkmarkCircle : closeCircle} />
            <span>{isCorrect ? 'Correct! 🎉' : 'Not quite! 🤔'}</span>
            {isCorrect && <span className="points-earned">+{points} pts</span>}
          </div>

          {!isCorrect && (
            <p className="correct-answer">
              The correct answer is: <strong>{correctAnswerText}</strong>
            </p>
          )}

          {explanation && (
            <div className="explanation-section">
              {!showExplanation ? (
                <IonButton 
                  fill="clear" 
                  size="small"
                  onClick={() => setShowExplanation(true)}
                >
                  <IonIcon icon={bulbOutline} slot="start" />
                  Show Explanation
                </IonButton>
              ) : (
                <div className="explanation-content">
                  <IonIcon icon={bulbOutline} />
                  <p>{explanation}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="quiz-actions">
        {!showResult ? (
          <IonButton 
            expand="block" 
            onClick={handleSubmit}
            disabled={selectedIndex === null}
          >
            Check Answer
          </IonButton>
        ) : (
          <IonButton expand="block" onClick={handleNext}>
            Continue
          </IonButton>
        )}
      </div>
    </div>
  );
}

export default QuizRenderer;